<template>
  <div class="page-settings-sidebar-wrap" @error="handleError">
    <div class="page-settings-sidebar" :class="{ 'is-loading': isLoading }">
      <div class="page-settings-sidebar__inner">
        <template v-if="activePage">
          <div class="page-settings-sidebar__heading">
            <div class="page-settings-sidebar__heading-icon">
              <BaseIcon name="split" size="16" />
            </div>
            <div class="page-settings-sidebar__heading-label">Page logic</div>
          </div>
          <div v-if="error" class="page-settings-sidebar__error" role="alert">
            {{ error }}
            <button class="close-btn" @click="clearError">&times;</button>
          </div>
          <div class="page-settings-sidebar__content">
            <PageLogicSettings
              :page="activePage"
              :form="form"
              @update-settings="updatePageSettings"
              @save-error="handleSaveError"
            />
          </div>
          <div class="page-settings-sidebar__footer">
            <Btn @click="closePageSettings" variant="outlined" :disabled="isLoading">Close</Btn>
          </div>
        </template>
        <div v-if="isLoading" class="page-settings-sidebar__loading">
          <LoadingSpinner />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { ref, computed, watch, onBeforeUnmount } from 'vue';
  import { useFormStore } from '@/store/formStore';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import PageLogicSettings from './PageLogicSettings.vue';

  export default {
    name: 'SidebarPage',

    components: {
      BaseIcon,
      Btn,
      LoadingSpinner,
      PageLogicSettings,
    },

    props: {
      activePage: {
        type: Object,
        required: true,
      },
    },

    setup(props) {
      const formStore = useFormStore();
      const error = ref(null);
      const isLoading = ref(false);
      const isSaving = ref(false);

      const form = computed(() => formStore.currentForm);

      // Handle settings updates
      const updatePageSettings = async (newSettings) => {
        try {
          isLoading.value = true;
          await formStore.updatePageSettings({
            ...newSettings,
            id: props.activePage.id,
          });
        } catch (err) {
          handleError(err);
        } finally {
          isLoading.value = false;
        }
      };

      // Error handling
      const handleError = (err) => {
        error.value = err.message || 'An error occurred';
        console.error(err);
      };

      const handleSaveError = (err) => {
        handleError(err);
      };

      const clearError = () => {
        error.value = null;
      };

      // Close sidebar
      const closePageSettings = () => {
        formStore.unsetPageSettings();
      };

      // Cleanup on unmount
      onBeforeUnmount(() => {
        clearError();
      });

      // Watch for prop changes
      watch(
        () => props.activePage,
        (newPage) => {
          if (newPage) {
            clearError();
          }
        }
      );

      return {
        form,
        error,
        isLoading,
        isSaving,
        updatePageSettings,
        closePageSettings,
        handleError,
        handleSaveError,
        clearError,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .page-settings-sidebar {
    width: 500px;
    max-width: 100%;
    overflow-y: auto;

    &.is-loading {
      pointer-events: none;
      opacity: 0.7;
    }

    &__inner {
      padding: 1rem 1rem 5rem 1rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__heading {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      @include card('sm');
    }

    &__heading-icon {
      width: 2rem;
      height: 2rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.25rem;
      background-color: var(--brand-50);
      color: var(--brand-700);
    }

    &__error {
      padding: 0.75rem;
      background-color: var(--error-50);
      color: var(--error-700);
      border-radius: 0.5rem;
      margin-top: 1rem;
      position: relative;

      .close-btn {
        position: absolute;
        right: 0.5rem;
        top: 0.5rem;
        background: none;
        border: none;
        cursor: pointer;
        color: var(--error-700);
      }
    }
    &__content {
      position: relative;
      box-shadow:
        0 0 rgba(0, 0, 0, 0),
        0 0 rgba(0, 0, 0, 0),
        0 1px 2px 0 rgba(0, 0, 0, 0.05);
      background-color: #ffffff;
      padding: 1.5rem;
      border-radius: 0.5rem;
      padding: 1rem;
      padding: 10px 1rem;
    }
  }
</style>
